import { roleName, WIN_NAMES, phaseName } from './format';

export interface LogEntry {
  seq: number;
  at: string;
  type: string;
  data: Record<string, unknown>;
}

const CAUSE_TEXT: Record<string, string> = {
  VAMPIRE: 'von den Vampiren',
  POISON: 'am Gift der Hexe',
  DAY_VOTE: 'durch das Urteil des Dorfes',
  LINKED: 'aus Liebe',
  HUNTRESS: 'durch die Vampirjägerin',
  ADMIN: 'durch eine Korrektur des Spielleiters',
};

const ACTION_TEXT: Record<string, string> = {
  VAMPIRE_ATTACK: 'greift an',
  SEER_INVESTIGATE: 'prüft',
  PRIEST_PROTECT: 'schützt',
  HUNTRESS_TAKE: 'reißt mit ins Jenseits',
  DAY_VOTE: 'stimmt für',
  MAYOR_TIEBREAK: 'entscheidet für',
  MAYOR_SUCCESSOR: 'übergibt das Amt an',
  DEMON_POSSESS: 'macht besessen',
};

/**
 * Übersetzt ein Ereignis in einen Satz. Namen werden aufgelöst, damit im Protokoll nicht
 * nur Kennungen stehen. Unbekannte Ereignisse werden weggelassen – das Protokoll soll lesbar
 * bleiben und nicht jede innere Regung des Servers zeigen.
 */
export function describeEvent(entry: LogEntry, nameOf: (id: unknown) => string): string | null {
  const d = entry.data ?? {};
  const list = (ids: unknown): string => (Array.isArray(ids) ? ids.map(nameOf).join(', ') : '—');

  switch (entry.type) {
    case 'PLAYER_JOINED':
      return `${nameOf(d.playerId)} ist beigetreten.`;
    case 'PLAYER_RENAMED':
      return `${String(d.before ?? '—')} heißt jetzt ${String(d.after ?? '—')}.`;
    case 'PLAYER_REMOVED':
      return `${nameOf(d.playerId)} wurde entfernt${d.reason ? ` (${String(d.reason)})` : ''}.`;
    case 'PLAYER_PRESENCE_CHANGED':
      return `${nameOf(d.playerId)}: Status ${d.presence === 'PAUSED' ? 'pausiert' : 'aktiv'}.`;
    case 'ROLES_ASSIGNED':
      return 'Die Rollen wurden verteilt.';
    case 'LOVERS_REVEALED':
      return `Die Geliebten sind ${list(d.playerIds ?? d.lovers)}.`;
    case 'DRUNK_REVEALED':
      return `${nameOf(d.playerId)} wird nüchtern und ist ${roleName(String(d.role ?? ''))}.`;
    case 'NIGHT_STARTED':
      return `Nacht ${String(d.number ?? '')} beginnt.`;
    case 'ACTION_CONFIRMED': {
      const verb = ACTION_TEXT[String(d.kind)];
      const payload = (d.payload ?? {}) as Record<string, unknown>;
      if (d.kind === 'WITCH_POTIONS') {
        const teile: string[] = [];
        if (payload.heal) teile.push('setzt den Heiltrank ein');
        if (payload.poisonTargetId) teile.push(`vergiftet ${nameOf(payload.poisonTargetId)}`);
        return `${nameOf(d.actorId)} ${teile.length > 0 ? teile.join(' und ') : 'setzt keinen Trank ein'}.`;
      }
      if (d.kind === 'GHOST_LETTER') return `Der Geist ${nameOf(d.actorId)} schreibt „${String(payload.letter ?? '')}“.`;
      if (!verb) return null;
      return `${nameOf(d.actorId)} ${verb} ${nameOf(payload.targetId)}.`;
    }
    case 'SEER_RESULT':
      return `${nameOf(d.seerId)} prüft ${nameOf(d.targetId)}: ${d.result === 'EVIL' ? 'verdächtig' : 'unverdächtig'}.`;
    case 'PROTECTION':
      return `${nameOf(d.priestId)} schützt ${nameOf(d.targetId)}.`;
    case 'POSSESSION':
      return `Besessen: ${list(d.playerIds)}.`;
    case 'NIGHT_RESOLVED': {
      const deaths = Array.isArray(d.deaths) ? (d.deaths as { playerId: string; causes: string[] }[]) : [];
      const blocked = Array.isArray(d.blocked) ? (d.blocked as { playerId: string; cause: string; blockedBy: string }[]) : [];
      const teile: string[] = [];
      if (deaths.length === 0) teile.push('niemand stirbt');
      else {
        teile.push(
          deaths
            .map((x) => `${nameOf(x.playerId)} stirbt ${x.causes.map((c) => CAUSE_TEXT[c] ?? c).join(' und ')}`)
            .join('; '),
        );
      }
      for (const b of blocked) {
        const wodurch = b.blockedBy === 'HEAL' ? 'der Heiltrank' : 'der Priester';
        const was = b.cause === 'POSSESSION' ? 'die Besessenheit' : `den Tod ${CAUSE_TEXT[b.cause] ?? b.cause}`;
        teile.push(`${wodurch} verhindert bei ${nameOf(b.playerId)} ${was}`);
      }
      return `Nacht ausgewertet: ${teile.join('; ')}.`;
    }
    case 'GHOST_APPOINTED':
      return `${nameOf(d.playerId)} wird zum Geist.`;
    case 'GHOST_LETTER_WRITTEN':
      return `Der Geist hinterlässt „${String(d.letter ?? '')}“ – noch nicht freigegeben.`;
    case 'GHOST_LETTER_REVEALED':
      return `Buchstabe des Geistes freigegeben: „${String(d.letter ?? '')}“ (bisher ${list(d.letters)}).`;
    case 'MORNING_REVEAL':
      return d.noVictim ? 'Der Morgen bringt keine Toten.' : `Am Morgen gefunden: ${list(d.deadPlayerIds)}.`;
    case 'DEATH':
      return `${nameOf(d.playerId)} stirbt ${Array.isArray(d.causes) ? d.causes.map((c) => CAUSE_TEXT[String(c)] ?? String(c)).join(' und ') : ''}.`;
    case 'HUNTRESS_TAKE':
      return `Die Vampirjägerin ${nameOf(d.actorId ?? d.playerId)} nimmt ${nameOf(d.targetId)} mit.`;
    case 'VOTE':
      return `${nameOf(d.voterId ?? d.actorId)} stimmt für ${nameOf(d.targetId)}.`;
    case 'VOTES_REVEALED':
      return 'Die Stimmen wurden aufgedeckt.';
    case 'DAY_VOTE_RESULT': {
      if (d.type === 'ELIMINATED') return `${nameOf(d.playerId)} wurde herausgewählt.`;
      if (d.type === 'TIE_NO_MAYOR') return 'Gleichstand ohne Bürgermeister – niemand stirbt.';
      if (d.type === 'MAYOR_TIEBREAK') return 'Gleichstand – der Bürgermeister entscheidet.';
      return 'Abstimmung ausgewertet.';
    }
    case 'MAYOR_CHANGED':
      return d.playerId ? `${nameOf(d.playerId)} ist Bürgermeister.` : 'Das Amt des Bürgermeisters ist unbesetzt.';
    case 'MAYOR_ELECTION_OPENED':
      return 'Bürgermeisterwahl geöffnet.';
    case 'MAYOR_ELECTION_CLOSED':
      return 'Bürgermeisterwahl geschlossen.';
    case 'MAYOR_ELECTION_SKIPPED':
      return 'Die Bürgermeisterwahl wurde übersprungen.';
    case 'TWITCH_MAYOR_VOTE':
      return `Chat: ${String(d.displayName ?? '—')} wählt Nummer ${String(d.playerNumber ?? '')}.`;
    case 'FOOL_WIN_TRIGGERED':
      return 'Der Narr wurde bei der ersten Abstimmung herausgewählt.';
    case 'FOOL_CONVERTED':
      return `${nameOf(d.playerId)} ist ab jetzt ein gewöhnlicher Dorfbewohner.`;
    case 'WIN_CONDITION':
      return `Siegbedingung erfüllt: ${Array.isArray(d.met) ? d.met.map((w) => WIN_NAMES[String(w)] ?? String(w)).join(', ') : ''}.`;
    case 'GAME_ENDED':
      return `Partie beendet – es gewinnt ${Array.isArray(d.winners) ? d.winners.map((w) => WIN_NAMES[String(w)] ?? String(w)).join(', ') : ''}.`;
    case 'ROUND_ABORTED':
      return `Die Runde wurde abgebrochen${d.reason ? ` (${String(d.reason)})` : ''}.`;
    case 'NEW_ROUND':
      return `Neue Runde (Partie ${String(d.gameNumber ?? '')}).`;
    case 'PAUSE_CHANGED':
      return d.paused ? 'Die Partie wurde angehalten.' : 'Die Partie läuft weiter.';
    case 'ADMIN_CHANGE':
      return `Korrektur: ${String(d.change ?? '')}${d.playerId ? ` bei ${nameOf(d.playerId)}` : ''}${
        d.reason ? ` (${String(d.reason)})` : ''
      }.`;
    case 'PROXY_ACTION':
      return `Der Spielleiter hat für ${nameOf(d.actorId)} entschieden.`;
    case 'NIGHT_STEP_FORCED':
      return `Phase ${phaseName(String(d.step ?? ''))} ohne Aktion beendet.`;
    default:
      return null;
  }
}
